import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { EVENT } from '../../../util/events'
import { keyed } from 'lit/directives/keyed.js'

@customElement('dependent-card')
export class DependentCard extends LitElement {
  static styles = css`
    .required-star {
      color: var(--lh-color-danger-600, #cc393b);
    }
  `
  @property({ type: Number })
  dependentId = 1

  // Display position label supplied by parent (1-based)
  @property({ type: Number })
  displayIndex = 1

  @property({ type: Boolean })
  dentalSelected = false

  @property({ type: Boolean })
  visionSelected = false

  @property({ type: String })
  dependentDob: string = ''

  // Persisted checkbox state from parent context
  @property({ type: Boolean })
  dentalChecked?: boolean

  @property({ type: Boolean })
  visionChecked?: boolean

  @state()
  private dependentDentalChecked = false

  @state()
  private dependentVisionChecked = false

  @state()
  private isOverAge = false

  get formattedDependentDob(): string {
    return this.formatDateToMMDDYYYY(this.dependentDob)
  }

  private sanitizeDobValue(date: string): string {
    if (!date) return ''
    return date.replace(/undefined/gi, '').replace(/\/+/g, '/')
  }

  private formatDateToMMDDYYYY(date: string): string {
    if (!date) return ''
    const sanitized = this.sanitizeDobValue(date)
    if (sanitized.includes('/')) return sanitized
    const parts = sanitized.split('-')
    if (parts.length === 3) {
      return this.sanitizeDobValue(`${parts[1]}/${parts[2]}/${parts[0]}`)
    }
    return sanitized
  }

  firstUpdated() {
    // Initialize from persisted state when available; otherwise default to plan selections
    this.dependentDentalChecked = this.dentalChecked ?? this.dentalSelected
    this.dependentVisionChecked = this.visionChecked ?? this.visionSelected
    this.updateAgeFlag(this.dependentDob)
    this.emitDataChange()
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties)

    if (changedProperties.has('dependentDob') && this.dependentDob) {
      const storedDob = this.dependentDob
      setTimeout(() => {
        if (!this.isConnected) return
        const dobInput = this.shadowRoot?.querySelector(
          `[name="dob-dependent-${this.dependentId}"]`
        ) as { change?: (value: string) => void } | null
        const iso = storedDob.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        dobInput?.change?.(iso ? `${iso[2]}/${iso[3]}/${iso[1]}` : storedDob)
      }, 0)
    }

    if (changedProperties.has('dentalSelected')) {
      this.dependentDentalChecked = this.dentalSelected
      this.emitDataChange()
      // Force re-render to update checkbox
      this.requestUpdate()
    }
    if (changedProperties.has('visionSelected')) {
      this.dependentVisionChecked = this.visionSelected
      this.emitDataChange()
      // Force re-render to update checkbox
      this.requestUpdate()
    }
    if (changedProperties.has('dentalChecked')) {
      this.dependentDentalChecked =
        typeof this.dentalChecked === 'boolean'
          ? this.dentalChecked
          : this.dependentDentalChecked
      this.requestUpdate()
    }
    if (changedProperties.has('visionChecked')) {
      this.dependentVisionChecked =
        typeof this.visionChecked === 'boolean'
          ? this.visionChecked
          : this.dependentVisionChecked
      this.requestUpdate()
    }
    if (changedProperties.has('dependentDob')) {
      this.updateAgeFlag(this.dependentDob)
      // reflect any changes to parent consumers
      this.emitDataChange()
    }
    // No automatic renumbering; parent controls IDs and labels
  }

  private handleCheckboxChange(event: CustomEvent) {
    const path = event.composedPath()
    const checkbox = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-checkbox'
    ) as HTMLElement

    if (checkbox) {
      const name = checkbox.getAttribute('name')
      const checked = (event as any).detail?.checked
      if (name === `dental-dependent-${this.dependentId}`) {
        this.dependentDentalChecked = checked
        this.emitDataChange()
      } else if (name === `vision-dependent-${this.dependentId}`) {
        this.dependentVisionChecked = checked
        this.emitDataChange()
      }
    }
  }

  private handleDobChange(event: CustomEvent) {
    const path = event.composedPath()
    const input = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-input'
    ) as HTMLElement

    if (
      input &&
      input.getAttribute('name') === `dob-dependent-${this.dependentId}`
    ) {
      const newDob = this.sanitizeDobValue((event as any).detail?.value || '')
      this.dependentDob = newDob
      // Calculate age and set isOverAge directly
      this.calculateAndSetOverAge(newDob)
      // Emit immediately with the just-calculated overAge value
      this.emitDataChange()
    }
  }

  private calculateAndSetOverAge(dobStr: string) {
    if (!dobStr) {
      this.isOverAge = false
      return
    }
    const today = new Date()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const yyyy = String(today.getFullYear())
    const todayStr = `${mm}/${dd}/${yyyy}`
    const age = this.calcAgeOnDate(dobStr, todayStr)
    // Check if date is in the future or age is 26 or older
    const dob = this.parseMMDDYYYY(dobStr)
    const dobIsInFuture = dob && dob > today
    this.isOverAge = dobIsInFuture || (age ?? 0) >= 26
  }

  private emitDataChange() {
    const dobInput = this.shadowRoot?.querySelector(
      `[name="dob-dependent-${this.dependentId}"]`
    ) as any
    this.dispatchEvent(
      new CustomEvent('dependent-data-change', {
        detail: {
          id: this.dependentId,
          dob: dobInput?.value || this.dependentDob || '',
          dentalChecked: this.dependentDentalChecked,
          visionChecked: this.dependentVisionChecked,
          overAge: this.isOverAge,
        },
        bubbles: true,
        composed: true,
      })
    )
  }

  private parseMMDDYYYY(dateStr: string): Date | null {
    if (!dateStr) return null
    const normalized = dateStr.includes('/')
      ? dateStr
      : this.formatDateToMMDDYYYY(dateStr)
    const parts = normalized.split('/')
    if (parts.length !== 3) return null
    const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10))
    if (!mm || !dd || !yyyy) return null
    return new Date(yyyy, mm - 1, dd)
  }

  private calcAgeOnDate(dobStr: string, onDateStr: string): number | null {
    const dob = this.parseMMDDYYYY(dobStr)
    const onDate = this.parseMMDDYYYY(onDateStr)
    if (!dob || !onDate) return null
    let age = onDate.getFullYear() - dob.getFullYear()
    const m = onDate.getMonth() - dob.getMonth()
    if (m < 0 || (m === 0 && onDate.getDate() < dob.getDate())) {
      age--
    }
    return age
  }

  private updateAgeFlag(dobStr: string) {
    if (!dobStr) {
      this.isOverAge = false
      return
    }
    const today = new Date()
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    const dd = String(today.getDate()).padStart(2, '0')
    const yyyy = String(today.getFullYear())
    const todayStr = `${mm}/${dd}/${yyyy}`
    const age = this.calcAgeOnDate(dobStr, todayStr)
    // Check if date is in the future or age is 26 or older
    const dob = this.parseMMDDYYYY(dobStr)
    const dobIsInFuture = dob && dob > today
    this.isOverAge = dobIsInFuture || (age ?? 0) >= 26
    this.requestUpdate()
  }

  private getDobErrorMessage(): string {
    if (!this.isOverAge) return ''
    const dob = this.parseMMDDYYYY(this.dependentDob)
    const today = new Date()
    if (dob && dob > today) {
      return 'Date of birth cannot be in the future.'
    }
    return 'Dependent children must be under 26 as of the coverage start date.'
  }

  private handleChange(event: CustomEvent) {
    const path = event.composedPath()
    const input = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-input'
    ) as HTMLElement
    const checkbox = path.find(
      (el) => (el as HTMLElement).tagName?.toLowerCase() === 'lh-checkbox'
    ) as HTMLElement

    if (input) {
      this.handleDobChange(event)
    } else if (checkbox) {
      this.handleCheckboxChange(event)
    }
  }

  private handleClose() {
    this.dispatchEvent(
      new CustomEvent('dependent-close', {
        detail: { id: this.dependentId },
        bubbles: true,
        composed: true,
      })
    )
  }

  connectedCallback() {
    super.connectedCallback()
    this.addEventListener(EVENT.LH_CLOSE, this.handleClose as EventListener)
    this.addEventListener(EVENT.LH_CHANGE, this.handleChange as EventListener)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener(EVENT.LH_CLOSE, this.handleClose as EventListener)
    this.removeEventListener(
      EVENT.LH_CHANGE,
      this.handleChange as EventListener
    )
  }

  render() {
    return html`
      <lh-grid>
        <lh-grid-item>
          <lh-container
            margin-top="sm"
            spacing="none"
            gutters="none"
            context-id="dependent-card-${this.dependentId}"
          >
            <lh-dismissible-card data-dependent-id="${this.dependentId}">
              <lh-typography size="text-">
                <strong>Dependent ${this.displayIndex}</strong>
              </lh-typography>
              <lh-grid>
                <lh-grid-item span="6">
                  <lh-input
                    name="dob-dependent-${this.dependentId}"
                    type="dob"
                    default-value=${this.formattedDependentDob}
                    error-message=${this.getDobErrorMessage()}
                    required
                  >
                    Date of birth
                  </lh-input>
                </lh-grid-item>
              </lh-grid>
              <lh-typography size="text-md">
                <strong
                  >Select the coverage for this applicant
                  <span class="required-star">*</span></strong
                >
              </lh-typography>
              ${keyed(
                this.dependentDentalChecked,
                html`<lh-checkbox
                  name="dental-dependent-${this.dependentId}"
                  value="dental"
                  ?disabled=${!this.dentalSelected}
                  ?default-checked=${this.dependentDentalChecked}
                >
                  Dental coverage
                </lh-checkbox>`
              )}
              ${!this.dentalSelected
                ? html`<lh-typography variant="p" size="text-md">
                    <em>
                      Primary subscriber must enroll in dental coverage to add
                      dental for a spouse or dependent.
                    </em>
                  </lh-typography>`
                : ''}
              ${keyed(
                this.dependentVisionChecked,
                html`<lh-checkbox
                  name="vision-dependent-${this.dependentId}"
                  value="vision"
                  ?disabled=${!this.visionSelected}
                  ?default-checked=${this.dependentVisionChecked}
                >
                  Vision coverage
                </lh-checkbox>`
              )}
              ${!this.visionSelected
                ? html`<lh-typography variant="p" size="text-md">
                    <em>
                      Primary subscriber must enroll in vision coverage to add
                      vision for a spouse or dependent.
                    </em>
                  </lh-typography>`
                : ''}
            </lh-dismissible-card>
          </lh-container>
        </lh-grid-item>
      </lh-grid>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dependent-card': DependentCard
  }
}
